import { Link } from 'react-router-dom';
import { heroMedia } from '../utils/mediaConfig.js';

const DataCenterConduit = () => (
  <>
  <section className="relative h-[860px] tablet:px-6 tablet:h-auto tablet:min-h-[560px]">
    <video autoPlay muted loop playsInline preload="metadata" poster={heroMedia.dataCenterConduit.poster} aria-hidden="true" className="absolute inset-0 w-full h-full object-cover">
      <source src={heroMedia.dataCenterConduit.video} type="video/mp4" />
    </video>
    <div className="absolute inset-0 [background:linear-gradient(90deg,_rgba(7,9,53,0.97)_0%,_rgba(7,9,53,0.8)_50%,_rgba(7,9,53,0.2)_100%)]">
    </div>
    <div className="relative pt-[240px] px-[120px] pb-0 max-w-[980px] flex flex-col gap-7 tablet:pt-[124px] tablet:px-6 tablet:pb-16">
      <p className="m-0 text-[16px] text-mx-accent-light tracking-[0.04em] font-display">
        <Link to="/data-centers" className="text-mx-accent-light">
          Data centers
        </Link>
        {' / Conduit'}
      </p>
      <h1 className="m-0 font-medium text-[72px] leading-[1.04] tracking-[-0.02em] font-display tablet:text-[44px] tablet:leading-[1.08]">
        Conduit, accessories and assembled systems for data centers
      </h1>
      <p className="m-0 text-[20px] leading-[1.6] text-mx-copy max-w-[720px]">
        Conduit, duct bank spacers, connectors and assembled systems, manufactured to your drawings and delivered on your build sequence, hall by hall.
      </p>
      <div className="flex gap-4 mt-2 phone:flex-wrap">
        <Link to="/contact" className="mx-button">
          Send us the drawing
        </Link>
        <Link to="#conduit-range" className="mx-button-outline">
          See the range
        </Link>
      </div>
    </div>
  </section>
  <section className="pt-[120px] px-[120px] pb-0 grid [grid-template-columns:1.2fr_1fr] gap-20 items-start tablet:px-6 tablet:[grid-template-columns:1fr]">
    <div className="flex flex-col gap-5 tablet:min-w-0">
      <h2 className="m-0 font-medium text-[44px] leading-[1.12] font-display tablet:text-[32px]">
        Engineered for the pace of hyperscale construction
      </h2>
      <p className="m-0 text-[18px] leading-[1.65] text-mx-copy">
        Data center programs repeat the same scopes across multiple halls and multiple sites. We manufacture to that reality: repeatable parts, documented quality, and releases aligned to your construction sequence rather than a catalog stocking cycle.
      </p>
    </div>
    <div className="flex flex-col gap-7 tablet:min-w-0">
      <div className="flex flex-col gap-2 pl-5 border-l-2 border-mx-accent">
        <span className="font-semibold text-[14px] tracking-[0.14em] uppercase text-mx-accent-light font-display">
          One source
        </span>
        <p className="m-0 text-[16px] leading-[1.6] text-mx-copy">
          Conduit, spacers, connectors and assemblies on one PO, not four vendors and four lead times.
        </p>
      </div>
      <div className="flex flex-col gap-2 pl-5 border-l-2 border-mx-accent">
        <span className="font-semibold text-[14px] tracking-[0.14em] uppercase text-mx-accent-light font-display">
          20 ft & 40 ft
        </span>
        <p className="m-0 text-[16px] leading-[1.6] text-mx-copy">
          Standard straight lengths in HDPE and PVC, plus continuous coils and custom lengths.
        </p>
      </div>
      <div className="flex flex-col gap-2 pl-5 border-l-2 border-mx-accent">
        <span className="font-semibold text-[14px] tracking-[0.14em] uppercase text-mx-accent-light font-display">
          Certified
        </span>
        <p className="m-0 text-[16px] leading-[1.6] text-mx-copy">
          Certificates of compliance, material test reports and lot traceability with every shipment.
        </p>
      </div>
    </div>
  </section>
  <section id="conduit-range" className="pt-[140px] px-[120px] pb-0 flex flex-col gap-12 tablet:px-6">
    <div className="flex justify-between items-end gap-12 tablet:flex-wrap">
      <div className="flex flex-col gap-4 max-w-[700px]">
        <p className="m-0 text-[16px] text-mx-accent-light tracking-[0.04em] font-display">
          Product range
        </p>
        <h2 className="m-0 font-medium text-[52px] leading-[1.1] font-display tablet:text-[32px]">
          The complete accessory range, from one manufacturer
        </h2>
      </div>
      <p className="m-0 text-[18px] leading-[1.6] text-mx-muted max-w-[480px]">
        A duct bank is far more than pipe. Spacers, couplers, end bells, plugs, hold-downs and pull tape all have to arrive together, fit each other and match the conduit they were specified against.
      </p>
    </div>
    <div className="grid [grid-template-columns:repeat(3,_minmax(0,_1fr))] gap-6 tablet:[grid-template-columns:1fr]">
      <div className="p-8 bg-mx-card border border-mx-border rounded-[20px] flex flex-col gap-3.5 tablet:min-w-0">
        <h3 className="m-0 font-semibold text-[22px] leading-[1.25] font-display">
          HDPE Conduit & Duct
        </h3>
        <p className="m-0 text-[16px] leading-[1.6] text-mx-muted">
          High-density polyethylene conduit for direct-burial, duct bank and underground feeder runs where flexibility and long pulls matter.
        </p>
        <div className="flex flex-wrap gap-2 mt-1.5">
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Smooth Wall
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Ribbed Inner Wall
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            SDR / DR Series
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Co-Extruded Stripe
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Coils & Straights
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Pre-Lubricated
          </span>
        </div>
      </div>
      <div className="p-8 bg-mx-card border border-mx-border rounded-[20px] flex flex-col gap-3.5 tablet:min-w-0">
        <h3 className="m-0 font-semibold text-[22px] leading-[1.25] font-display">
          PVC Conduit & Fittings
        </h3>
        <p className="m-0 text-[16px] leading-[1.6] text-mx-muted">
          Rigid PVC for encased duct bank, above-grade and in-building routing, bell-end or plain-end, with matched fittings.
        </p>
        <div className="flex flex-wrap gap-2 mt-1.5">
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Schedule 40
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Schedule 80
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Type EB
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Type DB
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Bell End
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Elbows & Sweeps
          </span>
        </div>
      </div>
      <div className="p-8 bg-mx-card border border-mx-border rounded-[20px] flex flex-col gap-3.5 tablet:min-w-0">
        <h3 className="m-0 font-semibold text-[22px] leading-[1.25] font-display">
          Assembled Units & Systems
        </h3>
        <p className="m-0 text-[16px] leading-[1.6] text-mx-muted">
          Beyond loose conduit: pre-assembled and kitted systems so crews install rather than fabricate on site.
        </p>
        <div className="flex flex-wrap gap-2 mt-1.5">
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Duct Bank Racks
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Multi-Way Bundles
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Factory-Fitted Couplings
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Job Kitting
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Sequenced by Hall
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Custom Sub-Assemblies
          </span>
        </div>
      </div>
      <div className="p-8 bg-mx-card border border-mx-border rounded-[20px] flex flex-col gap-3.5 tablet:min-w-0">
        <h3 className="m-0 font-semibold text-[22px] leading-[1.25] font-display">
          Duct Bank Spacers & Chairs
        </h3>
        <p className="m-0 text-[16px] leading-[1.6] text-mx-muted">
          Snap-together spacers that hold conduit arrays on grid through trenching and the concrete pour, sized to the conduit OD and spacing on your print.
        </p>
        <div className="flex flex-wrap gap-2 mt-1.5">
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Base Chairs
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Intermediate Chairs
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Bore Spacers
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Stackable
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            High-Impact Polymer
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Direct-Burial Rated
          </span>
        </div>
      </div>
      <div className="p-8 bg-mx-card border border-mx-border rounded-[20px] flex flex-col gap-3.5 tablet:min-w-0">
        <h3 className="m-0 font-semibold text-[22px] leading-[1.25] font-display">
          Couplers & Connectors
        </h3>
        <p className="m-0 text-[16px] leading-[1.6] text-mx-muted">
          Mechanical joints for HDPE and PVC, in the styles crews already install, gasketed for water and air resistance.
        </p>
        <div className="flex flex-wrap gap-2 mt-1.5">
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Push-Lock
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Band-Clamp
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Split Couplings
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Threaded
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Reducers
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Material Transitions
          </span>
        </div>
      </div>
      <div className="p-8 bg-mx-card border border-mx-border rounded-[20px] flex flex-col gap-3.5 tablet:min-w-0">
        <h3 className="m-0 font-semibold text-[22px] leading-[1.25] font-display">
          Terminations & End Bells
        </h3>
        <p className="m-0 text-[16px] leading-[1.6] text-mx-muted">
          Where the duct meets the vault, pad or building wall, matched to the conduit run they ship with.
        </p>
        <div className="flex flex-wrap gap-2 mt-1.5">
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            End Bells
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Bell-Mouth
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Adapters
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Elbows & Sweeps
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Wall Entry
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Vault Entry
          </span>
        </div>
      </div>
      <div className="p-8 bg-mx-card border border-mx-border rounded-[20px] flex flex-col gap-3.5 tablet:min-w-0">
        <h3 className="m-0 font-semibold text-[22px] leading-[1.25] font-display">
          End Caps & Duct Plugs
        </h3>
        <p className="m-0 text-[16px] leading-[1.6] text-mx-muted">
          Keeps water, soil and debris out of the duct between installation and cable pull.
        </p>
        <div className="flex flex-wrap gap-2 mt-1.5">
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Flat End Caps
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Tapered End Caps
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Cable End Caps
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Expansion Plugs
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Split Design
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Multi-Way Plugs
          </span>
        </div>
      </div>
      <div className="p-8 bg-mx-card border border-mx-border rounded-[20px] flex flex-col gap-3.5 tablet:min-w-0">
        <h3 className="m-0 font-semibold text-[22px] leading-[1.25] font-display">
          Pull Tape & Lubricants
        </h3>
        <p className="m-0 text-[16px] leading-[1.6] text-mx-muted">
          Supplied pre-installed in the conduit or shipped loose with the order.
        </p>
        <div className="flex flex-wrap gap-2 mt-1.5">
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Woven Polyester
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Detectable Tape
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Aramid High-Tensile
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Footage Marking
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Pulling Lubricants
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Pre-Lubricated Duct
          </span>
        </div>
      </div>
      <div className="p-8 bg-mx-card border border-mx-border rounded-[20px] flex flex-col gap-3.5 tablet:min-w-0">
        <h3 className="m-0 font-semibold text-[22px] leading-[1.25] font-display">
          Hold-Downs & Racking
        </h3>
        <p className="m-0 text-[16px] leading-[1.6] text-mx-muted">
          Hardware that keeps the duct bank where it was set while concrete goes in, plus nonmetallic hardware for manholes and vaults.
        </p>
        <div className="flex flex-wrap gap-2 mt-1.5">
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Hold-Down Bars
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Anti-Float
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Nonmetallic Strapping
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Anchor Stakes
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Cable Racks
          </span>
          <span className="px-2.5 py-1.5 border border-mx-border-light rounded-lg text-[13px] text-mx-nav-text">
            Hooks & Standoffs
          </span>
        </div>
      </div>
    </div>
    <p className="m-0 text-[16px] leading-[1.6] text-mx-quiet max-w-[900px]">
      Accessory sizes, materials and joint styles are built to your specification. If a drawing calls out a competitor part number, send it over: we quote the equivalent or manufacture to the print.
    </p>
  </section>
  <section className="pt-[140px] px-[120px] pb-0 flex flex-col gap-10 tablet:px-6">
    <div className="flex justify-between items-end gap-12 tablet:flex-wrap">
      <div className="flex flex-col gap-4 max-w-[700px]">
        <p className="m-0 text-[16px] text-mx-accent-light tracking-[0.04em] font-display">
          Specifications
        </p>
        <h2 className="m-0 font-medium text-[52px] leading-[1.1] font-display tablet:text-[32px]">
          Standard production parameters
        </h2>
      </div>
      <p className="m-0 text-[18px] leading-[1.6] text-mx-muted max-w-[480px]">
        Configurations outside these ranges are reviewed case by case. Send us the drawing.
      </p>
    </div>
    <div className="overflow-x-auto border border-mx-border rounded-[20px] bg-mx-panel">
      <table className="w-full min-w-[640px] border-collapse">
        <thead>
          <tr>
            <th className="text-left px-5 py-4 font-semibold text-[14px] text-mx-accent-light border-b border-mx-border-light whitespace-nowrap font-display">
              Parameter
            </th>
            <th className="text-left px-5 py-4 font-semibold text-[14px] text-mx-accent-light border-b border-mx-border-light whitespace-nowrap font-display">
              HDPE
            </th>
            <th className="text-left px-5 py-4 font-semibold text-[14px] text-mx-accent-light border-b border-mx-border-light whitespace-nowrap font-display">
              PVC
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="px-5 py-4 font-semibold text-[16px] text-mx-white border-b border-mx-border align-top whitespace-nowrap font-display">
              Wall construction
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              SDR / DR series, smooth or ribbed inner wall
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Schedule 40, Schedule 80, Type EB, Type DB
            </td>
          </tr>
          <tr>
            <td className="px-5 py-4 font-semibold text-[16px] text-mx-white border-b border-mx-border align-top whitespace-nowrap font-display">
              Terminations
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Plain end; couplings and fusion joints available
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Bell end or plain end; matched fittings available
            </td>
          </tr>
          <tr>
            <td className="px-5 py-4 font-semibold text-[16px] text-mx-white border-b border-mx-border align-top whitespace-nowrap font-display">
              Color & marking
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Solid color or co-extruded stripe; custom print legend
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Standard gray or custom color; custom print legend
            </td>
          </tr>
          <tr>
            <td className="px-5 py-4 font-semibold text-[16px] text-mx-white border-b border-mx-border align-top whitespace-nowrap font-display">
              Options
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Pull tape, pre-lubrication, sequential footage marking
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Pull tape, sequential footage marking
            </td>
          </tr>
          <tr>
            <td className="px-5 py-4 font-semibold text-[16px] text-mx-white border-b border-mx-border align-top whitespace-nowrap font-display">
              Assembled units
            </td>
            <td colSpan="2" className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Pre-assembled duct bank sections, multi-way bundles, spacer assemblies and job-kitted systems, built to customer drawings
            </td>
          </tr>
          <tr>
            <td className="px-5 py-4 font-semibold text-[16px] text-mx-white border-b border-mx-border align-top whitespace-nowrap font-display">
              Packaging
            </td>
            <td colSpan="2" className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Banded bundles, reels, coils or custom pallet configurations sequenced for site delivery
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p className="m-0 text-[15px] leading-[1.6] text-mx-quiet">
      Diameters, SDR/schedule combinations and custom lengths available on request. Contact engineering to confirm availability for a specific size and specification.
    </p>
  </section>
  <section className="pt-[140px] px-[120px] pb-0 flex flex-col gap-10 tablet:px-6">
    <div className="flex justify-between items-end gap-12 tablet:flex-wrap">
      <div className="flex flex-col gap-4 max-w-[700px]">
        <p className="m-0 text-[16px] text-mx-accent-light tracking-[0.04em] font-display">
          One package
        </p>
        <h2 className="m-0 font-medium text-[52px] leading-[1.1] font-display tablet:text-[32px]">
          Buy the system, not the pieces
        </h2>
      </div>
      <p className="m-0 text-[18px] leading-[1.6] text-mx-muted max-w-[480px]">
        What the market typically splits across three or four vendors, we deliver as one contract-manufactured package.
      </p>
    </div>
    <div className="overflow-x-auto border border-mx-border rounded-[20px] bg-mx-panel">
      <table className="w-full min-w-[640px] border-collapse">
        <thead>
          <tr>
            <th className="text-left px-5 py-4 font-semibold text-[14px] text-mx-accent-light border-b border-mx-border-light whitespace-nowrap font-display">
              Scope
            </th>
            <th className="text-left px-5 py-4 font-semibold text-[14px] text-mx-accent-light border-b border-mx-border-light whitespace-nowrap font-display">
              Typical market approach
            </th>
            <th className="text-left px-5 py-4 font-semibold text-[14px] text-mx-accent-light border-b border-mx-border-light whitespace-nowrap font-display">
              MachineX America
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="px-5 py-4 font-semibold text-[16px] text-mx-white border-b border-mx-border align-top whitespace-nowrap font-display">
              Conduit & duct
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Catalog SKUs from a large extruder
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-white border-b border-mx-border align-top [background:rgba(232,119,58,0.08)]">
              Built to your print: length, wall, color, marking
            </td>
          </tr>
          <tr>
            <td className="px-5 py-4 font-semibold text-[16px] text-mx-white border-b border-mx-border align-top whitespace-nowrap font-display">
              Spacers & chairs
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Separate specialty supplier, separate lead time
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-white border-b border-mx-border align-top [background:rgba(232,119,58,0.08)]">
              Produced in-house, sized to your conduit and spacing
            </td>
          </tr>
          <tr>
            <td className="px-5 py-4 font-semibold text-[16px] text-mx-white border-b border-mx-border align-top whitespace-nowrap font-display">
              Couplers & fittings
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Third vendor; tolerance matching is the contractor's problem
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-white border-b border-mx-border align-top [background:rgba(232,119,58,0.08)]">
              Matched to the duct in the same order
            </td>
          </tr>
          <tr>
            <td className="px-5 py-4 font-semibold text-[16px] text-mx-white border-b border-mx-border align-top whitespace-nowrap font-display">
              Assembly
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Fabricated on site by the installing crew
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-white border-b border-mx-border align-top [background:rgba(232,119,58,0.08)]">
              Pre-assembled and job-kitted at the factory
            </td>
          </tr>
          <tr>
            <td className="px-5 py-4 font-semibold text-[16px] text-mx-white border-b border-mx-border align-top whitespace-nowrap font-display">
              Documentation
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Certificates collected from each supplier separately
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-white border-b border-mx-border align-top [background:rgba(232,119,58,0.08)]">
              One certification package covering the whole shipment
            </td>
          </tr>
          <tr>
            <td className="px-5 py-4 font-semibold text-[16px] text-mx-white border-b border-mx-border align-top whitespace-nowrap font-display">
              Scheduling
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-copy border-b border-mx-border align-top">
              Multiple lead times to coordinate
            </td>
            <td className="px-5 py-4 text-[16px] leading-[1.5] text-mx-white border-b border-mx-border align-top [background:rgba(232,119,58,0.08)]">
              One release schedule tied to your construction sequence
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
  <section className="pt-[140px] px-[120px] pb-0 flex flex-col gap-10 tablet:px-6">
    <div className="flex justify-between items-end gap-12 tablet:flex-wrap">
      <div className="flex flex-col gap-4 max-w-[700px]">
        <p className="m-0 text-[16px] text-mx-accent-light tracking-[0.04em] font-display">
          Standards
        </p>
        <h2 className="m-0 font-medium text-[52px] leading-[1.1] font-display tablet:text-[32px]">
          Certified to applicable standards
        </h2>
      </div>
      <p className="m-0 text-[18px] leading-[1.6] text-mx-muted max-w-[480px]">
        Product is manufactured and certified to the standards applicable to the specified configuration. Certificates of compliance and test documentation are issued with every shipment.
      </p>
    </div>
    <div className="grid [grid-template-columns:repeat(3,_minmax(0,_1fr))] gap-6 tablet:[grid-template-columns:1fr]">
      <div className="p-7 border border-mx-border rounded-[20px] flex flex-col gap-2.5 tablet:min-w-0">
        <span className="text-[13px] text-mx-accent-light font-mono">
          Solid-Wall HDPE
        </span>
        <h3 className="m-0 font-semibold text-[24px] font-display">
          ASTM F2160
        </h3>
        <p className="m-0 text-[15px] leading-[1.6] text-mx-muted">
          Solid-wall HDPE conduit based on controlled outside diameter.
        </p>
      </div>
      <div className="p-7 border border-mx-border rounded-[20px] flex flex-col gap-2.5 tablet:min-w-0">
        <span className="text-[13px] text-mx-accent-light font-mono">
          PE Pressure Pipe
        </span>
        <h3 className="m-0 font-semibold text-[24px] font-display">
          ASTM D3035
        </h3>
        <p className="m-0 text-[15px] leading-[1.6] text-mx-muted">
          Polyethylene pressure pipe, controlled outside diameter.
        </p>
      </div>
      <div className="p-7 border border-mx-border rounded-[20px] flex flex-col gap-2.5 tablet:min-w-0">
        <span className="text-[13px] text-mx-accent-light font-mono">
          Coilable HDPE
        </span>
        <h3 className="m-0 font-semibold text-[24px] font-display">
          NEMA TC-7
        </h3>
        <p className="m-0 text-[15px] leading-[1.6] text-mx-muted">
          Smooth-wall coilable HDPE conduit for underground installation.
        </p>
      </div>
      <div className="p-7 border border-mx-border rounded-[20px] flex flex-col gap-2.5 tablet:min-w-0">
        <span className="text-[13px] text-mx-accent-light font-mono">
          Rigid PVC
        </span>
        <h3 className="m-0 font-semibold text-[24px] font-display">
          NEMA TC-2
        </h3>
        <p className="m-0 text-[15px] leading-[1.6] text-mx-muted">
          Rigid PVC conduit, Schedule 40 and Schedule 80.
        </p>
      </div>
      <div className="p-7 border border-mx-border rounded-[20px] flex flex-col gap-2.5 tablet:min-w-0">
        <span className="text-[13px] text-mx-accent-light font-mono">
          PVC Utilities Duct
        </span>
        <h3 className="m-0 font-semibold text-[24px] font-display">
          NEMA TC-6 & 8
        </h3>
        <p className="m-0 text-[15px] leading-[1.6] text-mx-muted">
          PVC plastic utilities duct for underground installation.
        </p>
      </div>
      <div className="p-7 border border-mx-border rounded-[20px] flex flex-col gap-2.5 tablet:min-w-0">
        <span className="text-[13px] text-mx-accent-light font-mono">
          Listed Conduit
        </span>
        <h3 className="m-0 font-semibold text-[24px] font-display">
          UL 651
        </h3>
        <p className="m-0 text-[15px] leading-[1.6] text-mx-muted">
          Schedule 40 and 80 rigid PVC conduit and fittings.
        </p>
      </div>
    </div>
    <p className="m-0 text-[15px] leading-[1.6] text-mx-quiet max-w-[900px]">
      Standards listed reflect the specifications commonly applied to HDPE and PVC conduit. The certifications supplied with any given order correspond to that product's configuration and intended installation. Confirm required listings with our team during quoting.
    </p>
  </section>
  <section className="pt-[140px] px-[120px] pb-0 flex flex-col gap-12 tablet:px-6">
    <h2 className="m-0 font-medium text-[52px] leading-[1.1] max-w-[820px] font-display tablet:text-[32px]">
      From drawing to delivered pallets in five steps
    </h2>
    <div className="grid [grid-template-columns:repeat(5,_minmax(0,_1fr))] gap-7 tablet:[grid-template-columns:1fr]">
      <div className="flex flex-col gap-2.5 pt-5 border-t border-mx-accent tablet:min-w-0">
        <span className="font-medium text-[32px] text-mx-accent-light font-display">
          1
        </span>
        <h3 className="m-0 font-semibold text-[19px] font-display">
          Specification review
        </h3>
        <p className="m-0 text-[15px] leading-[1.6] text-mx-muted">
          You send drawings, specs and required listings. We confirm feasibility, material and applicable standards.
        </p>
      </div>
      <div className="flex flex-col gap-2.5 pt-5 border-t border-mx-accent tablet:min-w-0">
        <span className="font-medium text-[32px] text-mx-accent-light font-display">
          2
        </span>
        <h3 className="m-0 font-semibold text-[19px] font-display">
          Quote & sample
        </h3>
        <p className="m-0 text-[15px] leading-[1.6] text-mx-muted">
          Firm pricing against volume and schedule, with first-article samples for approval before production release.
        </p>
      </div>
      <div className="flex flex-col gap-2.5 pt-5 border-t border-mx-accent tablet:min-w-0">
        <span className="font-medium text-[32px] text-mx-accent-light font-display">
          3
        </span>
        <h3 className="m-0 font-semibold text-[19px] font-display">
          Production
        </h3>
        <p className="m-0 text-[15px] leading-[1.6] text-mx-muted">
          Extrusion, assembly and kitting under documented process control with in-line dimensional verification.
        </p>
      </div>
      <div className="flex flex-col gap-2.5 pt-5 border-t border-mx-accent tablet:min-w-0">
        <span className="font-medium text-[32px] text-mx-accent-light font-display">
          4
        </span>
        <h3 className="m-0 font-semibold text-[19px] font-display">
          Certification
        </h3>
        <p className="m-0 text-[15px] leading-[1.6] text-mx-muted">
          Certificates of compliance, material test reports and lot traceability compiled per shipment.
        </p>
      </div>
      <div className="flex flex-col gap-2.5 pt-5 border-t border-mx-accent tablet:min-w-0">
        <span className="font-medium text-[32px] text-mx-accent-light font-display">
          5
        </span>
        <h3 className="m-0 font-semibold text-[19px] font-display">
          Delivery
        </h3>
        <p className="m-0 text-[15px] leading-[1.6] text-mx-muted">
          Packaged and sequenced to your construction schedule, direct to site or to your consolidation point.
        </p>
      </div>
    </div>
  </section>
  <section className="mt-[140px] mx-[120px] mb-0 py-[72px] px-20 bg-mx-accent rounded-[28px] flex justify-between items-center gap-12 tablet:px-6 tablet:mx-4 tablet:flex-wrap">
    <div className="flex flex-col gap-3 text-mx-ink">
      <h2 className="m-0 font-semibold text-[44px] leading-[1.1] font-display tablet:text-[32px]">
        Send us the drawing
      </h2>
      <p className="m-0 text-[18px] leading-[1.6] max-w-[640px]">
        Tell us the size, specification, quantity and schedule. We come back with feasibility, certification scope and pricing, for a single line item or a full campus program.
      </p>
    </div>
    <Link to="/contact" className="inline-flex items-center min-h-14 px-8 py-0 rounded-[999px] bg-mx-ink text-white font-semibold text-[16px] whitespace-nowrap font-display">
      Request a quote
    </Link>
  </section>
  </>
);
export default DataCenterConduit;
